import { DeletedFilter, getGoalProgressGoalIds } from '@griply/utils';
import {
  useActiveTasks,
  useAllCompletedTasks,
  useHabits,
  useRepeatingTasks,
  useTasks,
} from 'features/tasks';
import React, { useEffect, useMemo } from 'react';
import {
  deriveAnyDocumentShowsCompleted,
  deriveTaskCounts,
  TaskCounts,
} from 'shared/utils/derive-task-counts';

import { useTaskCountsContext } from './use-task-counts-context';

export type TaskCountsResolverProps = {
  onCounts: (counts: TaskCounts) => void;
};

export const TaskCountsResolver: React.FC<TaskCountsResolverProps> = ({
  onCounts,
}) => {
  const context = useTaskCountsContext();

  // Derived before the completed subscription is enabled below, so the gate
  // never reads its own output.
  const anyShowsCompleted = useMemo(
    () => deriveAnyDocumentShowsCompleted(context),
    [context],
  );

  const activeTasks = useActiveTasks();
  const completedTasks = useAllCompletedTasks(anyShowsCompleted);
  const habits = useHabits({ deletedFilter: DeletedFilter.IncludeDeleted });
  const repeatingTasks = useRepeatingTasks({
    deletedFilter: DeletedFilter.IncludeDeleted,
  });

  // Empty unless the account has a project or a completed-tasks goal — then nothing subscribes.
  const goalProgressGoalIds = useMemo(
    () => getGoalProgressGoalIds(context.goals),
    [context.goals],
  );
  const goalProgressTasks = useTasks(
    { goals: goalProgressGoalIds },
    !!goalProgressGoalIds.length,
  );

  const counts = useMemo(
    () =>
      deriveTaskCounts({
        ...context,
        activeTasks,
        completedTasks,
        habits,
        repeatingTasks,
        goalProgressTasks,
      }),
    [
      context,
      activeTasks,
      completedTasks,
      habits,
      repeatingTasks,
      goalProgressTasks,
    ],
  );

  // Effect-bridge: publish the derived counts to the provider that sits ABOVE
  // this component's Suspense boundary. Per-query `suspense: false` is
  // type-blocked in the pinned TanStack 5.0.0, so the resolver must live under
  // Suspense to consume the task subscriptions — and the only way to hand the
  // result to the non-suspending provider (without re-suspending every badge
  // reader) is to lift it through state. Deliberate, not the state-as-cache
  // anti-pattern: `counts` is derived, never a mirror of a single query field.
  useEffect(() => {
    onCounts(counts);
  }, [counts, onCounts]);

  return null;
};
